const BREAKPOINTS = {
  DESKTOP: 1024,
  DESKTOP_LARGE: 1280
};

const SETTINGS = {
  THROTTLE_DELAY_SMALL: 50,
  THROTTLE_DELAY: 500,
  SEARCH_DEBOUNCE_DELAY: 300,
  FILTER_UPDATE_DELAY: 250,
  FILTER_DRAW_ANIMATION_TIME: 300,
  MOBILE_HEADER_STICKY_SCROLL_THRESHOLD: 100,
  NAV_TRANSITION_DURATION: 300,
  SCROLL_END_TOLERANCE: 8
};

const CART = {
  FORM_SUBMIT_TIMEOUT: 2000
};

const PRODUCT_RECENTLY_VIEWED = {
  MINIMUM_PRODUCTS_TO_SHOW: 3,
  MAX_CONTENT_CHARS_AMOUNT: 26
};

const CLASSES = {
  MINICART_OPEN: 'minicart-open',
  TRANSLATE_X_0: 'translate-x-0',
  TRANSLATE_X_100: 'translate-x-full',
  TRANSLATE_X_LEFT_100: '-translate-x-full',
  OVERFLOW_HIDDEN: 'overflow-hidden',
  SCROLLABLE: 'overflow-y-auto',
  INVISIBLE: 'invisible',
  OPACITY_0: 'opacity-0'
};

const ARIAS = {
  EXPANDED: 'aria-expanded'
};

const ATTRIBUTES = {
  INERT: 'inert',
  MEGA_DISMISSED: 'data-mega-dismissed',
  SEARCH_ACTIVE: 'data-search-active',
  VARIANT_SOLD_OUT: 'data-variant-sold-out'
};

const SWIPER = {
  CONTAINER_CLASS: '.swiper',
  SLIDE_CLASS: '.swiper-slide',
  SLIDE_ACTIVE_CLASS: '.swiper-slide-active'
};

const WISHLIST = {
  APP_ADDED_CLASSES: ['unbookmarked', 'bookmarked', 'sw_tooltip', 'tooltip', 'tooltipstered']
};

const SEARCH = {
  RESOURCE_TYPES: 'query,product,collection',
  LIMIT_SCOPE: 'each',
  SECTION_ID: 'predictive-search',
  REQUEST_LIMIT: 10,
  DEFAULT_SETTINGS: { trigger: 3, fields: 'title', oos: 'show' }
};

const EVENTS = {
  // Fired on document after every minicart render; third parties (Bloomreach) re-inject on it
  MINICART_RENDERED: 'minicart:rendered',
  // Fired on document after the cart page section re-renders (same cue for the cart page container)
  CART_RENDERED: 'cart:rendered',
  // Internal: asks the cart page to re-render after an add made outside its own controls
  CART_CHANGED: 'cart:changed',
  COLLECTION_RERENDER: 'collection:rerender',
  // PDP variant picker → detail: { variantId }
  VARIANT_CHANGED: 'variant:changed'
};

const COMPARE = {
  // localStorage keys match the previous theme's, so selections carry over
  MODE_KEY: 'compare',
  ITEMS_KEY: 'compareItems',
  // Also the column count in sections/compare.liquid and compare_max in sections/collection.liquid
  MAX_ITEMS: 4,
  // Alternate product view fetched into the compare page (templates/product.compare.liquid)
  VIEW: 'compare'
};

const DELIVERY_COUNTDOWN = {
  TIME_ZONE: 'Europe/London',
  BANK_HOLIDAYS_URL: 'https://www.gov.uk/bank-holidays.json',
  BANK_HOLIDAYS_REGION: 'england-and-wales',
  STORAGE_KEY: 'ukBankHolidays',
  TICK_MS: 1000,
  FETCH_TIMEOUT_MS: 5000,
  WEEKDAYS: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  DAY: { SUNDAY: 0, MONDAY: 1, THURSDAY: 4, FRIDAY: 5, SATURDAY: 6 },
  // Days from today to the next working day, keyed by weekday (Fri → Mon, Sat → Mon)
  NEXT_WORKING_DAY_OFFSET: { 5: 3, 6: 2, DEFAULT: 1 },
  WINDOW: { NEXT_DAY: 0, FRIDAY_CUT_OFF: 1, MONDAY_CUT_OFF: 2 }
};

const SELECTORS = {
  LOADING_SPINNER: '[data-loading-spinner]',
  HEADER: '[data-header]',
  NAV_TOGGLE: '[data-nav-toggle]',
  NAV_CLOSE: '[data-nav-close]',
  NAV_PARENT: '[data-nav-parent]',
  NAV_PANEL: '[data-nav-panel]',
  NAV_SCROLL_PARENT: '[data-nav-panel], [data-mobile-nav]',
  MOBILE_NAV: '[data-mobile-nav]',
  PANEL_OPEN: '[data-panel-open]',
  PANEL_BACK: '[data-panel-back]',
  SEARCH_PANEL: '[data-header-search]',
  SEARCH_TOGGLE: '[data-search-toggle]',
  SEARCH_CLOSE: '[data-search-close]',
  SEARCH_OVERLAY: '[data-search-overlay]',
  SEARCH_INPUT: '[data-search-input]',
  SEARCH_DEFAULT: '[data-search-default]',
  SEARCH_RESULTS: '[data-search-results]',
  SEARCH_PRODUCTS: '[data-search-products]',
  SEARCH_FOOTER: '[data-search-footer]',
  SEARCH_RESULTS_LINK: '[data-search-results-link]',
  SEARCH_STATUS: '[data-search-status]',
  SWIPER_DESKTOP: '[data-swiper-desktop]',
  PRODUCT_CARD: '[data-product-card]',
  SEARCH_SECTION: '#shopify-section-predictive-search',
  TABS: '[data-tabs]',
  TAB: '[data-tab]',
  TAB_PANEL: '[data-tab-panel]'
};

const AGE_VERIFICATION = {
  STORAGE_KEY: 'agePopupConfirmed',
  MODE_FULL_DATE: 'full_date',
  ERROR_INCOMPLETE: 'incomplete',
  ERROR_AGE: 'age'
};

const QUICK_BUY = {
  // Alternate product template the dialog fetches (templates/product.atc-popup.liquid)
  VIEW: 'atc-popup',
  TRIGGER_SELECTOR: '[data-quick-buy]',
  LEARN_MORE_SELECTOR: '[data-quick-buy-learn-more]'
};

const SHOP_THE_LOOK = {
  // Inline-layout product view for the PDP panel (templates/product.shop-the-look.liquid)
  VIEW: 'shop-the-look'
};

const PRODUCT_CARD = {
  // Fallback cap for the collection grid card carousel; the rendered gallery
  // carries the real one in data-gallery-limit (product-card-gallery.liquid)
  GALLERY_LIMIT: 5
};

const SELECT_OPTIONS = {
  // Box thumbnail render size (2x the 64px tile), set on the variant image
  // src when a linked box's colour changes (product-select-options.js)
  THUMBNAIL_SIZE: 128,
  THUMBNAIL_CROP: 'center',
  // Markup hooks shared by product-select-options.js and the box snippet
  BOX_SELECTOR: '[data-select-option-box]',
  BOX_INPUT_SELECTOR: '[data-select-option]',
  OPTION_INPUT_SELECTOR: '[data-select-option-input]',
  PANEL_SELECTOR: '[data-select-option-panel]',
  // The page product's own picker radios (product-variants.liquid)
  PAGE_INPUT_SELECTOR: '[data-variant-input]',
  QUANTITY_INPUT_SELECTOR: '[data-quantity-input]'
};

const KEYS = {
  ESCAPE: 'Escape',
  ARROW_LEFT: 'ArrowLeft',
  ARROW_RIGHT: 'ArrowRight',
  HOME: 'Home',
  END: 'End'
};

export {
  BREAKPOINTS,
  SETTINGS,
  CART,
  PRODUCT_RECENTLY_VIEWED,
  CLASSES,
  ARIAS,
  ATTRIBUTES,
  SWIPER,
  SEARCH,
  WISHLIST,
  SELECTORS,
  EVENTS,
  COMPARE,
  KEYS,
  AGE_VERIFICATION,
  DELIVERY_COUNTDOWN,
  QUICK_BUY,
  SHOP_THE_LOOK,
  PRODUCT_CARD,
  SELECT_OPTIONS
};
